/**
 * Fase 6 (2026-07-01) — Servicio de tandas segmentadas.
 * Consume los endpoints backend:
 *   GET  /api/cocina/pedido/:pedidoId/tandas
 *   POST /api/cocina/pedido/:pedidoId/tanda/:segmento/iniciar
 *   POST /api/cocina/pedido/:pedidoId/tanda/:segmento/terminar
 *   POST /api/cocina/pedido/:pedidoId/tanda/:segmento/enviar-a-despacho
 *   POST /api/despacho/pedido/:pedidoId/tanda/:segmento/entregar
 */

import { api } from './api';

export type Segmento = 'dia' | 'noche' | 'transversal';
export type EstadoSegmento =
  | 'solicitado'
  | 'en_preparacion'
  | 'listo'
  | 'entregado_a_despacho'
  | 'despachado'
  | 'cancelado';

export interface TandaSnapshot {
  segmento: Segmento;
  estado_seg: EstadoSegmento;
  n_registros: number;
  estados_cr: string | null;
}

export interface IngredienteFaltanteMsg {
  ingrediente_id: number;
  nombre: string;
  cantidad_necesaria: number;
  stock_disponible: number;
  unidad: string;
  faltante: number;
}

export interface TandaResultado {
  pedido_id: number;
  segmento: Segmento;
  registros_iniciados?: number;
  registros_transicionados?: number;
  estado_segmento?: EstadoSegmento;
}

export interface TandaError409 {
  pedido_id: number;
  segmento: Segmento;
  cocina_registro_id: number;
  ingredientes_faltantes: IngredienteFaltanteMsg[];
}

class CocinaTandaService {
  async listarTandas(pedidoId: number): Promise<TandaSnapshot[]> {
    const r = await api.get(`/cocina/pedido/${pedidoId}/tandas`);
    return (r.data?.data as TandaSnapshot[]) ?? [];
  }

  private async postTanda(
    pedidoId: number,
    segmento: Segmento,
    accion: 'iniciar' | 'terminar' | 'enviar-a-despacho'
  ): Promise<TandaResultado> {
    const r = await api.post(
      `/cocina/pedido/${pedidoId}/tanda/${segmento}/${accion}`,
      {}
    );
    return r.data?.data as TandaResultado;
  }

  iniciar(pedidoId: number, segmento: Segmento): Promise<TandaResultado> {
    return this.postTanda(pedidoId, segmento, 'iniciar');
  }
  terminar(pedidoId: number, segmento: Segmento): Promise<TandaResultado> {
    return this.postTanda(pedidoId, segmento, 'terminar');
  }
  enviarADespacho(
    pedidoId: number,
    segmento: Segmento
  ): Promise<TandaResultado> {
    return this.postTanda(pedidoId, segmento, 'enviar-a-despacho');
  }

  async entregarAlCliente(
    pedidoId: number,
    segmento: Segmento
  ): Promise<TandaResultado> {
    const r = await api.post(
      `/despacho/pedido/${pedidoId}/tanda/${segmento}/entregar`,
      {}
    );
    return r.data?.data as TandaResultado;
  }
}

export const cocinaTandaService = new CocinaTandaService();
export default cocinaTandaService;
